import React, { useState, useRef, useEffect } from 'react';
import get from 'lodash.get';
import Dropdown from 'react-dropdown';
import TouchScrollable from 'react-scrolllock';
import {
    getSortedForms,
    getGroupedForms,
    getFormByGradeAndSkillName,
} from '../utils/prepareFormsData';
import { constructDibels8Link, linkOnClickHandler, PAGE_TYPE } from '../navigation/navigation';
import { MEASURE_BY_SKILL_NAME, ASSESSMENT_TYPE } from '../../constants/assessments';
import { useAppContext } from '../contexts/AppContext';
import Popup from './Popup';
import CloseButton from '../buttons/CloseButton';
import Button from '../buttons/Button';
import RadioButton from '../buttons/RadioButton';
import { DIBELS8 } from '../../constants/applications';
import { usePopupContext } from '../contexts/PopupContext';
import { timesFormWasUsedInResults } from '../utils/popupDataHelper';
import { getStudentResults, getAllStudentResults } from '../utils/studentsListHelpers';
import { getNextForm } from '../recommendation-rules/getNextForm';
import FORM_NAMES_MAP, { FORM_SOURCE } from '../../constants/forms';
import { getMeasureResults } from '../utils/alternateFormsHelpers';
import { ALT_FORM_MESSAGE as MESSAGE } from '../../constants/popups';
import { formLegendOption, getFormOptions } from './utils';

import { openOnEnterKeyPressedOnDropdown } from '../utils/dropDownHelpers';

const D8AlternateFormSelectionPopup = () => {
    const {
        forms: {
            [DIBELS8]: { [ASSESSMENT_TYPE.PM]: rawForms },
        },
        checkAndGetSessionStatus,
        classId,
        allResults,
        formsMap,
    } = useAppContext();
    const {
        params: { grade, studentId, period, skillName, onFormSelected, measureName },
        actions: { hideD8AlternateFormSelectionPopup },
    } = usePopupContext();

    const dropdownRef = useRef(null);

    const isDevEnv = process.env.NODE_ENV === 'development';
    const dashboardPrefix = isDevEnv ? '' : 'dashboard-';

    const formSelectionSelector = `.${dashboardPrefix}alt-form-selection-dropdown-wrapper`;

    const formSelectionDropdown = document.querySelector(formSelectionSelector);

    useEffect(() => {
        if (formSelectionDropdown) {
            formSelectionDropdown.addEventListener(
                'keydown',
                openOnEnterKeyPressedOnDropdown(dropdownRef, formSelectionDropdown, true),
            );
        }

        return () => {
            if (formSelectionDropdown) {
                formSelectionDropdown.removeEventListener(
                    'keydown',
                    openOnEnterKeyPressedOnDropdown(dropdownRef, formSelectionDropdown, true),
                );
            }
        };
    }, [formSelectionDropdown]);

    const formsBySkill = getGroupedForms(rawForms, skillName);
    const forms = getFormByGradeAndSkillName(grade, skillName, formsBySkill);
    const [selectedFormSource, setSelectedFormSource] = useState(FORM_SOURCE.BM.id);

    const studentResults = getStudentResults(
        { [DIBELS8]: get(allResults, DIBELS8, {}) },
        studentId,
        getAllStudentResults,
    );
    const measureResults = getMeasureResults(studentResults[DIBELS8], skillName);

    const [selectedFormUID, setSelectedForm] = useState(() =>
        get(getNextForm(forms, measureResults), 'uid', ''),
    );

    const sortedFormsBySequence = getSortedForms(forms);

    const sortedForms = getSortedForms(forms).map(formBySkill => ({
        formUid: formBySkill.uid,
        formName: get(FORM_NAMES_MAP, formBySkill.uid, `Form ${formBySkill.sequence}`),
        timesUsedInResults: timesFormWasUsedInResults(formBySkill.uid, studentResults[DIBELS8]),
    }));

    const formOptions = [
        formLegendOption,
        ...getFormOptions(sortedForms, measureName, sortedFormsBySequence),
    ];

    const getSelectedFormOption = formOptions.filter(
        formOption => formOption.value === selectedFormUID,
    )[0];

    const onOKButtonClick = e => {
        const assessmentLink = constructDibels8Link(
            studentId,
            MEASURE_BY_SKILL_NAME[skillName],
            PAGE_TYPE.REMINDERS,
            {
                ...(selectedFormSource === FORM_SOURCE.PM.id && { formUid: selectedFormUID }),
                period,
                classId,
            },
        );

        hideD8AlternateFormSelectionPopup();
        if (onFormSelected instanceof Function) {
            // We can only pass argumets that are meaningfull to business logic as like form's period, skillName, grade.
            const chosenForm =
                selectedFormSource === FORM_SOURCE.BM.id
                    ? {
                          grade,
                          skillName,
                          period,
                      }
                    : formsMap[selectedFormUID];
            onFormSelected(assessmentLink, chosenForm);
        } else {
            linkOnClickHandler(assessmentLink, checkAndGetSessionStatus)(e);
        }
    };

    const setBMSelected = () => {
        setSelectedFormSource(FORM_SOURCE.BM.id);
    };

    const setPMSelected = () => {
        setSelectedFormSource(FORM_SOURCE.PM.id);
    };

    return (
        <Popup
            header={
                <>
                    <p>Alternate Form Selection</p>
                    <CloseButton onClick={hideD8AlternateFormSelectionPopup} />
                </>
            }
            message={
                <>
                    {MESSAGE[0]}
                    <br />
                    {MESSAGE[1]}
                    <p>
                        <RadioButton
                            value={FORM_SOURCE.BM.id}
                            id={FORM_SOURCE.BM.id}
                            name={FORM_SOURCE.BM.name}
                            isChecked={selectedFormSource === FORM_SOURCE.BM.id}
                            onChange={setBMSelected}
                        />
                        <RadioButton
                            value={FORM_SOURCE.BM.id}
                            id={FORM_SOURCE.PM.id}
                            name={FORM_SOURCE.PM.name}
                            isChecked={selectedFormSource === FORM_SOURCE.PM.id}
                            onChange={setPMSelected}
                        />
                    </p>
                    <TouchScrollable>
                        <div
                            className='dashboard-alt-form-selection-dropdown-wrapper'
                            tabIndex={selectedFormSource === FORM_SOURCE.PM.id ? 0 : -1}
                        >
                            <Dropdown
                                controlClassName='dashboard-form-selection-dropdown'
                                arrowClassName='dashboard-form-selection-dropdown-arrow'
                                menuClassName='dashboard-form-selection-dropdown-menu'
                                options={formOptions}
                                value={getSelectedFormOption}
                                placeholder="Select form"
                                onChange={e => setSelectedForm(e.value)}
                                disabled={selectedFormSource !== FORM_SOURCE.PM.id}
                                ref={dropdownRef}
                            />
                        </div>
                    </TouchScrollable>
                </>
            }
            buttons={[<Button key="ok-button" onClick={onOKButtonClick} />]}
            cssClass="alt-form-selection-popup"
        />
    );
};

export default D8AlternateFormSelectionPopup;
